/**
 * レギュレーションブック。ブランドの言いかたを 1 か所に持つ。
 *
 * ## ここは導けない区画である
 *
 * トーン・禁止表現・呼びかたの統一は、どのデータ源からも導けない。人が書いた
 * ものだけが入るので、初期値も候補も出さない（出すとブランドが決めたことと
 * 区別できなくなる）。
 *
 * ## 断られた理由を必ず出す
 *
 * `userErrors` は `field` 付きで返るが、入力欄に紐づける口だけへ流すと
 * 誰にも見えないまま消える。区画の先頭にまとめて出す。
 *
 * ## 版は読み直したものを添える
 *
 * `expectedVersion` には、いま画面に出ている帳面の版をそのまま渡す。
 * 先に誰かが書いていれば CONFLICT が返り、読み直しを促す。
 */

import { useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  RegulationBookDocument,
  UpdateRegulationBookDocument,
  type RegulationBookFieldsFragment,
  type UpdateRegulationBookInput,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import {
  MAX_REGULATION_TEXT_LENGTH,
  REGULATION_NOT_APPLIED_YET_NOTE,
} from "../api-capabilities";
import { formatJstWithZone } from "../format-time";

interface PageUserError {
  readonly code: string;
  readonly message: string;
}

const EMPTY_CHANGE = {
  addTones: [],
  removeToneIds: [],
  addBannedPhrases: [],
  removeBannedPhraseIds: [],
  addWordingRules: [],
  removeWordingRuleIds: [],
};

export function RegulationBookPage() {
  const [result, refetch] = useQuery({
    query: RegulationBookDocument,
    // 帳面はほかの端末からも書き換わる。開き直したら読み直す
    requestPolicy: "cache-and-network",
  });
  const [mutationState, updateBook] = useMutation(UpdateRegulationBookDocument);
  const [userErrors, setUserErrors] = useState<PageUserError[]>([]);
  const [tone, setTone] = useState("");
  const [banned, setBanned] = useState("");
  const [use, setUse] = useState("");
  const [avoid, setAvoid] = useState("");

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const book: RegulationBookFieldsFragment = result.data.regulationBook;
  const busy = mutationState.fetching;

  const apply = (
    change: Partial<UpdateRegulationBookInput>,
    after: () => void,
  ): void => {
    void updateBook({
      input: { ...EMPTY_CHANGE, ...change, expectedVersion: book.version },
    }).then((response) => {
      if (response.error !== undefined) {
        setUserErrors([
          {
            code: "INTERNAL_ERROR",
            message: `保存できませんでした: ${String(response.error)}`,
          },
        ]);
        return;
      }
      const payload = response.data?.updateRegulationBook;
      if (payload === undefined) return;
      if (payload.userErrors.length > 0) {
        setUserErrors(
          payload.userErrors.map(({ code, message }) => ({ code, message })),
        );
        return;
      }
      setUserErrors([]);
      after();
      refetch({ requestPolicy: "network-only" });
    });
  };

  const errors =
    userErrors.length === 0 ? null : (
      <div className="vd-notice vd-notice--error" role="alert">
        {userErrors.map((error) => (
          <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
            {error.message}
          </p>
        ))}
        {userErrors.some((error) => error.code === "CONFLICT") ? (
          <Button onClick={() => refetch({ requestPolicy: "network-only" })}>
            最新を読み込む
          </Button>
        ) : null}
      </div>
    );

  return (
    <div className="vd-page">
      <PageHeader
        title="レギュレーションブック"
        description="ブランドの言いかたを 1 か所にまとめます。ここに書いたものだけが入り、推定して埋めることはしません。"
        titleExtra={<HelpIcon slug="knowledge/regulation" />}
      />

      <div className="vd-stack">
        {errors}

        <Card title="この帳面について" note={REGULATION_NOT_APPLIED_YET_NOTE}>
          <p style={{ margin: 0 }}>
            {book.version === 0
              ? "まだ 1 件も登録がありません。"
              : `${book.version} 回目の更新まで記録しています（最後の更新: ${formatJstWithZone(book.updatedAt)}）。`}
          </p>
        </Card>

        <Card
          title="トーン"
          note="読み手はお客さまひとりです。読み手ごとに書き分けません。"
        >
          {book.tones.length === 0 ? (
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              まだ登録がありません。
            </p>
          ) : (
            <ul className="vd-stack" style={{ listStyle: "none", padding: 0 }}>
              {book.tones.map((entry) => (
                <li key={entry.id} className="vd-row">
                  <span>{entry.text}</span>
                  <span className="vd-stat-note">
                    {entry.addedBy.displayName} ・{" "}
                    {formatJstWithZone(entry.addedAt)}
                  </span>
                  <Button
                    disabled={busy}
                    aria-label={`トーン「${entry.text}」を消す`}
                    onClick={() =>
                      apply({ removeToneIds: [entry.id] }, () => undefined)
                    }
                  >
                    消す
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="vd-field" style={{ marginTop: 12 }}>
            <label htmlFor="regulation-tone">トーンを足す</label>
            <input
              id="regulation-tone"
              className="vd-input"
              maxLength={MAX_REGULATION_TEXT_LENGTH}
              value={tone}
              onChange={(event) => setTone(event.target.value)}
            />
            <div className="vd-row">
              <Button
                variant="primary"
                disabled={busy || tone.trim() === ""}
                onClick={() =>
                  apply({ addTones: [tone.trim()] }, () => setTone(""))
                }
              >
                トーンを足す
              </Button>
            </div>
          </div>
        </Card>

        <Card
          title="禁止表現"
          note="分からない項目には触れません。免責や母集団の注記は本文に書かず、根拠の側に残します。"
        >
          {book.bannedPhrases.length === 0 ? (
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              まだ登録がありません。
            </p>
          ) : (
            <ul className="vd-stack" style={{ listStyle: "none", padding: 0 }}>
              {book.bannedPhrases.map((entry) => (
                <li key={entry.id} className="vd-row">
                  <span>{entry.text}</span>
                  <span className="vd-stat-note">
                    {entry.addedBy.displayName} ・{" "}
                    {formatJstWithZone(entry.addedAt)}
                  </span>
                  <Button
                    disabled={busy}
                    aria-label={`禁止表現「${entry.text}」を消す`}
                    onClick={() =>
                      apply(
                        { removeBannedPhraseIds: [entry.id] },
                        () => undefined,
                      )
                    }
                  >
                    消す
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="vd-field" style={{ marginTop: 12 }}>
            <label htmlFor="regulation-banned">禁止表現を足す</label>
            <input
              id="regulation-banned"
              className="vd-input"
              maxLength={MAX_REGULATION_TEXT_LENGTH}
              value={banned}
              onChange={(event) => setBanned(event.target.value)}
            />
            <div className="vd-row">
              <Button
                variant="primary"
                disabled={busy || banned.trim() === ""}
                onClick={() =>
                  apply({ addBannedPhrases: [banned.trim()] }, () =>
                    setBanned(""),
                  )
                }
              >
                禁止表現を足す
              </Button>
            </div>
          </div>
        </Card>

        <Card title="呼びかたの統一">
          {book.wordingRules.length === 0 ? (
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              まだ登録がありません。
            </p>
          ) : (
            <div className="vd-table-scroll">
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>使う言いかた</th>
                    <th>使わない言いかた</th>
                    <th>登録</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {book.wordingRules.map((rule) => (
                    <tr key={rule.id}>
                      <th scope="row">{rule.use}</th>
                      <td>{rule.avoid}</td>
                      <td className="vd-stat-note">
                        {rule.addedBy.displayName} ・{" "}
                        {formatJstWithZone(rule.addedAt)}
                      </td>
                      <td>
                        <Button
                          disabled={busy}
                          aria-label={`呼びかた「${rule.use}」を消す`}
                          onClick={() =>
                            apply(
                              { removeWordingRuleIds: [rule.id] },
                              () => undefined,
                            )
                          }
                        >
                          消す
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="vd-field" style={{ marginTop: 12 }}>
            <label htmlFor="regulation-use">使う言いかた</label>
            <input
              id="regulation-use"
              className="vd-input"
              maxLength={MAX_REGULATION_TEXT_LENGTH}
              value={use}
              onChange={(event) => setUse(event.target.value)}
            />
            <label htmlFor="regulation-avoid">使わない言いかた</label>
            <input
              id="regulation-avoid"
              className="vd-input"
              maxLength={MAX_REGULATION_TEXT_LENGTH}
              value={avoid}
              onChange={(event) => setAvoid(event.target.value)}
            />
            <div className="vd-row">
              <Button
                variant="primary"
                disabled={busy || use.trim() === "" || avoid.trim() === ""}
                onClick={() =>
                  apply(
                    {
                      addWordingRules: [
                        { use: use.trim(), avoid: avoid.trim() },
                      ],
                    },
                    () => {
                      setUse("");
                      setAvoid("");
                    },
                  )
                }
              >
                呼びかたを足す
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
